import { FC, useEffect, useMemo, useState } from "react"
import axios from "axios"
import moment from "moment"
import {useTranslation} from "react-i18next"
import DatePicker, {registerLocale} from "react-datepicker"
import {de, enGB} from "date-fns/locale"
import "moment/locale/de"
import "moment/locale/en-gb"
import "react-datepicker/dist/react-datepicker.css"
import { Button, Card, Col, Container, Form, Row } from "react-bootstrap"

type Building = {
    identifier: string
    name: string
    campus: string
    campusMapUrl: string
    latitude: string
    longitude: string
    street: string
    postcode: string
    stopsNearby?: string[]
}

type TimeSlot = {
    group: string
    time: string
}

type PlatformKey =
    | "computerLab"
    | "personalDevice"
    | "smowl"
    | "examOnDemand"

type PlatformOption = {
    key: PlatformKey
    de: string
    en: string
}

type ExamLanguageKey =
    | "german"
    | "english"
    | "germanWithEnglishVersion"
    | "englishWithGermanVersion"
    | "germanWithEnglishAnswers"
    | "englishWithGermanAnswers"

type ExamLanguageOption = {
    key: ExamLanguageKey
    de: string
    en: string
}

type AidCategory = "digital" | "nonDigital"

type AidKey =
    | "deepL"
    | "pons"
    | "desmos"
    | "tuebingenCalculator"
    | "myScript"
    | "scratchPaper"
    | "broughtScratchPaper"
    | "nonProgrammableCalculator"
    | "formulaSheet"

type AidOption = {
    key: AidKey
    category: AidCategory
    de: string
    en: string
    content: string
}

type GuidelineCategory = "aids" | "technical" | "conduct"

type GuidelineRow = {
    category: GuidelineCategory
    label: string
    computerLab: string
    personalDevice: string
    smowl: string
    examOnDemand: string
}

type PreviewLanguage = "de" | "en"
type PreviewRole = "student" | "teacher"

type MoodlePreviewContext = {
    language: PreviewLanguage
    role: PreviewRole
    groups: Set<string>
    groupings: Set<string>
}

const STATIC_BASE_URL = "https://ondemand.e-learning.tu-darmstadt.de/static"

registerLocale("de", de)
registerLocale("en-GB", enGB)

const platformOptions: PlatformOption[] = [
    { key: "computerLab", de: "Prüfung im bereitgestellten Computerpoolraum", en: "Exam in a Provided Computer Lab" },
    { key: "personalDevice", de: "Prüfung vor Ort auf dem eigenen Gerät", en: "On-site Exam on a Personal Device" },
    { key: "examOnDemand", de: "Prüfung On Demand", en: "Exam On Demand" },
    { key: "smowl", de: "Fernaufsicht mit SMOWL", en: "Remote Proctoring with SMOWL" },
]

const examLanguageOptions: ExamLanguageOption[] = [
    {
        key: "german",
        de: "Die Aufgaben werden gemäß Modulhandbuch auf Deutsch gestellt; Antworten sind auf Deutsch zu verfassen.",
        en: "In accordance with the module handbook, the questions are provided in German; answers must be written in German.",
    },
    {
        key: "english",
        de: "Die Aufgaben werden gemäß Modulhandbuch auf Englisch gestellt; Antworten sind auf Englisch zu verfassen.",
        en: "In accordance with the module handbook, the questions are provided in English; answers must be written in English.",
    },
    {
        key: "germanWithEnglishVersion",
        de: "Die Aufgaben werden gemäß Modulhandbuch auf Deutsch gestellt und zusätzlich in englischer Übersetzung bereitgestellt; Antworten sind auf Deutsch zu verfassen.",
        en: "In accordance with the module handbook, the questions are provided in German with an additional English translation; answers must be written in German.",
    },
    {
        key: "englishWithGermanVersion",
        de: "Die Aufgaben werden gemäß Modulhandbuch auf Englisch gestellt und zusätzlich in deutscher Übersetzung bereitgestellt; Antworten sind auf Englisch zu verfassen.",
        en: "In accordance with the module handbook, the questions are provided in English with an additional German translation; answers must be written in English.",
    },
    {
        key: "germanWithEnglishAnswers",
        de: "Die Aufgaben werden gemäß Modulhandbuch auf Deutsch gestellt; Antworten dürfen auf Deutsch oder Englisch verfasst werden.",
        en: "In accordance with the module handbook, the questions are provided in German; answers may be written in German or English.",
    },
    {
        key: "englishWithGermanAnswers",
        de: "Die Aufgaben werden gemäß Modulhandbuch auf Englisch gestellt; Antworten dürfen auf Englisch oder Deutsch verfasst werden.",
        en: "In accordance with the module handbook, the questions are provided in English; answers may be written in English or German.",
    },
]

const mlang = (de: string, en: string) =>
    `{mlang de}${de}{mlang}{mlang other}${en}{mlang}`

const escapeHtml = (value: string) =>
    value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;")

const aidOptions: AidOption[] = [
    {
        key: "deepL",
        category: "digital",
        de: "DeepL Übersetzer",
        en: "DeepL Translator",
        content: `${mlang("Übersetzer", "Translator")}: <a href="https://www.deepl.com/de/translator" target="_blank" rel="noopener">DeepL</a>`,
    },
    {
        key: "pons",
        category: "digital",
        de: "PONS Wörterbuch",
        en: "PONS Dictionary",
        content: `${mlang("Wörterbuch", "Dictionary")}: <a href="https://de.pons.com/%C3%BCbersetzung" target="_blank" rel="noopener">PONS</a>`,
    },
    {
        key: "desmos",
        category: "digital",
        de: "Desmos Taschenrechner",
        en: "Desmos Calculator",
        content: `${mlang("Taschenrechner", "Calculator")}: <a href="https://www.desmos.com/scientific?lang=de" target="_blank" rel="noopener">Desmos</a>`,
    },
    {
        key: "tuebingenCalculator",
        category: "digital",
        de: "Taschenrechner der Universität Tübingen",
        en: "University of Tübingen Calculator",
        content: `${mlang("Taschenrechner", "Calculator")}: <a href="https://uni-tuebingen.de/fileadmin/Uni_Tuebingen/Fakultaeten/MathePhysik/Institute/IAAT/AIT/Tools/taschenrechner.html" target="_blank" rel="noopener">${mlang("Universität Tübingen", "University of Tübingen")}</a>`,
    },
    {
        key: "myScript",
        category: "digital",
        de: "MyScript Text Whiteboard",
        en: "MyScript Text Whiteboard",
        content: `${mlang("Digitales Schmierpapier", "Digital scratch paper")}: <a href="https://webdemo.myscript.com/views/text/index.html" target="_blank" rel="noopener">MyScript Text</a>`,
    },
    {
        key: "scratchPaper",
        category: "nonDigital",
        de: "Ausgehändigtes Schmierblatt",
        en: "Provided sheet of scratch paper",
        content: mlang(
            "Ein leeres Schmierblatt wird auf Anfrage ausgehändigt und muss am Ende abgegeben werden.",
            "A blank sheet of scratch paper is provided upon request and must be handed in at the end."
        ),
    },
    {
        key: "broughtScratchPaper",
        category: "nonDigital",
        de: "Mitgebrachtes Schmierblatt",
        en: "Personal sheet of scratch paper",
        content: mlang(
            "Ein leeres Schmierblatt darf mitgebracht werden und muss am Ende abgegeben werden.",
            "A blank sheet of scratch paper may be brought to the examination and must be handed in at the end."
        ),
    },
    {
        key: "nonProgrammableCalculator",
        category: "nonDigital",
        de: "Nicht programmierbarer Taschenrechner",
        en: "Non-programmable calculator",
        content: mlang(
            "Ein nicht programmierbarer Taschenrechner ohne Speicher darf mitgebracht werden.",
            "A non-programmable calculator without memory may be brought to the examination."
        ),
    },
    {
        key: "formulaSheet",
        category: "nonDigital",
        de: "Freigegebene Formelsammlung",
        en: "Approved formula sheet",
        content: mlang(
            "Die für die Prüfung freigegebene Formelsammlung darf verwendet werden.",
            "The formula sheet approved for the examination may be used."
        ),
    },
]

const rooms = [
    "L1|10 101",
    "L1|10 108",
    "L1|10 201",
    "L1|10 206",
    "L4|02 1+2",
    "L4|02 335",
    "L4|02 201+202",
    "L5|01 130",
    "S1|01 A01",
    "S1|01 A02",
    "S1|01 A03",
    "S1|01 A04",
    "S1|05 122",
    "S1|22 020",
    "S2|02 C003",
    "S2|02 C205",
    "S2|02 C005",
    "S2|06 30",
    "S3|06 51",
    "S3|06 52",
    "S3|06 53",
    "S3|11 0012",
    "S3|11 08",
    "S3|13 36",
    "GU W|12 1OG78",
    "GU W|12 1OG83",
    "L2|03 06b",
    "L2|03 6",
    "L3|01 50",
    "S1|01 A1",
    "S1|01 A4",
]

const renderMoodlePreview = (
    source: string,
    context: MoodlePreviewContext
) => {
    const conditionMatches = (
        condition: string,
        argument: string | undefined
    ) => {
        const name = argument?.trim() ?? ""

        switch (condition) {
            case "ifstudent":
                return context.role === "student"
            case "ifteacher":
                return context.role === "teacher"
            case "ifingroup":
                return context.groups.has(name)
            case "ifnotingroup":
                return !context.groups.has(name)
            case "ifingrouping":
                return context.groupings.has(name)
            case "ifnotingrouping":
                return !context.groupings.has(name)
            default:
                return false
        }
    }

    type ConditionFrame = {
        condition: string
        argument?: string
        openingTag: string
        content: string
    }

    const conditionTagPattern =
        /\{(\/?)(ifnotingrouping|ifingrouping|ifnotingroup|ifingroup|ifstudent|ifteacher)(?:\s+([^}]+))?\}/g
    const frames: ConditionFrame[] = [{
        condition: "",
        openingTag: "",
        content: "",
    }]
    let sourceIndex = 0
    let match: RegExpExecArray | null

    while ((match = conditionTagPattern.exec(source)) !== null) {
        frames[frames.length - 1].content += source.slice(
            sourceIndex,
            match.index
        )
        sourceIndex = conditionTagPattern.lastIndex

        const [tag, closingMarker, condition, argument] = match

        if (!closingMarker) {
            frames.push({
                condition,
                argument,
                openingTag: tag,
                content: "",
            })
            continue
        }

        const frame = frames[frames.length - 1]

        if (frames.length > 1 && frame.condition === condition) {
            frames.pop()
            if (conditionMatches(frame.condition, frame.argument)) {
                frames[frames.length - 1].content += frame.content
            }
        } else {
            frames[frames.length - 1].content += tag
        }
    }

    frames[frames.length - 1].content += source.slice(sourceIndex)

    while (frames.length > 1) {
        const frame = frames.pop()!
        frames[frames.length - 1].content +=
            frame.openingTag + frame.content
    }

    let rendered = frames[0].content

    rendered = rendered.replace(
        /\{mlang (de|other)\}([\s\S]*?)\{mlang\}/g,
        (_, language: "de" | "other", content: string) =>
            (context.language === "de" && language === "de") ||
            (context.language === "en" && language === "other")
                ? content
                : ""
    )

    return rendered
}

const slug = (value: string) =>
    value
        .toLowerCase()
        .replaceAll("|", "_")
        .replaceAll(" ", "_")
        .replaceAll("+", "_")
        .replace(/[^a-z0-9_]/g, "")

const roomGroup = (room: string) => slug(room)

const buildingGrouping = (identifier: string) => slug(identifier)

const getBuildingIdentifierFromRoom = (room: string) => room.split(" ")[0]

const osmEmbedUrl = (building: Building) => {
    const lat = Number(building.latitude)
    const lng = Number(building.longitude)

    return `https://www.openstreetmap.org/export/embed.html?bbox=${lng - 0.001}%2C${lat - 0.001}%2C${lng + 0.001}%2C${lat + 0.001}&amp;layer=mapnik&amp;marker=${lat}%2C${lng}`
}

const osmLargeUrl = (building: Building) =>
    `https://www.openstreetmap.org/?mlat=${building.latitude}&amp;mlon=${building.longitude}#map=19/${building.latitude}/${building.longitude}`

const renderExternalContentMap = (building: Building) => {
    const uid = `map_${slug(building.identifier)}`
    const src = osmEmbedUrl(building)
    const link = osmLargeUrl(building)

    return `<div id="${uid}"><div class="external-map-placeholder card text-center bg-light text-dark" style="height: 450px; display: flex; justify-content: center; align-items: center; border: 1px solid #dee2e6;"><div class="card-body" style="max-width: 650px;"><div style="font-size: 3rem; margin-bottom: 1rem;">🌍</div><h5 class="card-title">${mlang("OpenStreetMap laden", "Load OpenStreetMap")}</h5><p class="card-text text-muted">${mlang("Die Karte wird erst nach Klick geladen.", "The map will only be loaded after clicking the button.")}</p><button type="button" class="btn btn-primary" onclick="this.closest('#${uid}').querySelector('iframe').src='${src}'; this.closest('#${uid}').querySelector('.external-map-placeholder').style.display='none'; this.closest('#${uid}').querySelector('.external-map-loaded').style.display='block';">${mlang("Karte laden", "Load map")}</button><div class="mt-3 text-muted" style="font-size: .8rem;">${mlang("Beim Laden werden Inhalte von OpenStreetMap eingebunden.", "Loading this map embeds content from OpenStreetMap.")}</div></div></div><div class="external-map-loaded" style="display: none;"><iframe title="OpenStreetMap" src="" width="100%" height="450" loading="lazy" referrerpolicy="strict-origin-when-cross-origin" style="border: 1px solid #dee2e6; border-radius: 0.25rem; background-color: #ffffff;"></iframe><div class="mt-2 text-right"><a href="${link}" target="_blank" rel="noopener noreferrer" class="btn btn-primary"><i class="bi bi-box-arrow-up-right"></i> ${mlang("Größere Karte anzeigen", "Show larger map")}</a></div></div></div>`
}

const renderBuildingCard = (building: Building) => {
    const stops = building.stopsNearby ?? []
    const imageUrl = `${STATIC_BASE_URL}/${building.identifier.replace("|", "").toLowerCase()}.jpg`

    const stopBadges = stops.length
        ? `<div>${stops.map(stop => `<span class="badge badge-secondary mr-1 mb-1">${stop}</span>`).join(" ")}</div>`
        : `<div class="text-muted">${mlang("Keine Haltestellen hinterlegt.", "No public transport stops stored.")}</div>`

    return `<div class="card my-3"><div class="card-header"><h4><i class="bi bi-buildings"></i> ${mlang("Gebäude", "Building")}</h4></div><div class="card-body"><div class="row"><div class="col-12 col-md-4 mb-3 mb-md-0"><div class="position-relative"><img src="${imageUrl}" alt="${building.name}" class="img-fluid rounded" style="width: 100%; max-height: 320px; object-fit: cover;"><span class="badge badge-danger position-absolute" style="top: 1rem; left: 1rem; font-size: 1rem;">${building.identifier}</span></div></div><div class="col-12 col-md-8"><h4 class="mb-1">${building.name}</h4><div class="text-muted mb-3">${mlang(`Standort ${building.campus}`, `${building.campus} campus`)}</div><div class="border rounded p-3 mb-3"><div class="text-muted small mb-1"><i class="bi bi-sign-turn-slight-right"></i> ${mlang("Adresse", "Address")}</div><div>${building.street}</div><div>${building.postcode}</div></div><div class="border rounded p-3 mb-3"><div class="text-muted small mb-1"><i class="bi bi-geo-alt"></i> ${mlang("Koordinaten", "Coordinates")}</div><div>${building.latitude}, ${building.longitude}</div></div><div class="border rounded p-3 mb-3"><div class="text-muted small mb-2"><i class="bi bi-train-front"></i> ${mlang("ÖPNV", "Public transport")}</div>${stopBadges}</div><a class="btn btn-outline-secondary" href="${building.campusMapUrl}" target="_blank" rel="noopener noreferrer"><i class="bi bi-box-arrow-up-right"></i> ${mlang("Campus-Information", "Campus information")}</a></div></div></div></div><div class="card my-3"><div class="card-header"><h4><i class="bi bi-map"></i> ${mlang("Karte", "Map")}</h4></div><div class="card-body">${renderExternalContentMap(building)}</div></div>`
}

const renderGuidelinesTable = (
    selectedPlatforms: PlatformKey[],
    restrictSmowlToChoiceGroup: boolean,
    arrivalMinutes: number,
    examLanguage: ExamLanguageKey,
    examDate: string,
    examDurationMinutes: number,
    timeSlots: TimeSlot[],
    selectedAidKeys: AidKey[]
) => {
    const columns = platformOptions.filter(platform =>
        selectedPlatforms.includes(platform.key)
    )

    if (!columns.length) {
        return ""
    }

    const cell = (content: string, colSpan: number) =>
        `<td${colSpan > 1 ? ` colspan="${colSpan}"` : ""} style="padding: .75rem; vertical-align: top;">${content}</td>`

    const platformCell = (platform: PlatformOption, content: string) => {
        if (platform.key === "smowl" && restrictSmowlToChoiceGroup) {
            return `{ifingroup choice_smowl}${content}{/ifingroup}`
        }

        return content
    }

    const headerCell = (platform: PlatformOption) =>
        platformCell(
            platform,
            `<th style="padding: .75rem; vertical-align: middle;">${mlang(platform.de, platform.en)}</th>`
        )

    const bookedExamWindow = mlang(
        "Beginn und Ende richten sich nach dem gebuchten Termin.",
        "The start and end times are determined by the booked appointment."
    )
    const entryOffset = Number.isFinite(arrivalMinutes)
        ? Math.max(0, arrivalMinutes)
        : 0
    const duration = Number.isFinite(examDurationMinutes)
        ? Math.max(0, examDurationMinutes)
        : 0
    const iterationSchedule = timeSlots.map((slot, index) => {
        const start = moment(
            `${examDate} ${slot.time}`,
            "YYYY-MM-DD HH:mm",
            true
        )
        const end = start.clone().add(duration, "minutes")
        const admission = start.clone().subtract(entryOffset, "minutes")
        const iteration = mlang(
            `Iteration ${index + 1}`,
            `Iteration ${index + 1}`
        )
        const iterationLabel = timeSlots.length > 1
            ? `<strong>${iteration}</strong><br>`
            : ""

        return {
            examWindow: `<div${index > 0 ? ' style="margin-top: .5rem;"' : ""}>${iterationLabel}${mlang(
                `${start.locale("de").format("D. MMMM YYYY, HH:mm")} bis ${end.locale("de").format("HH:mm [Uhr]")}`,
                `${start.locale("en-gb").format("D MMMM YYYY, HH:mm")} to ${end.locale("en-gb").format("HH:mm")}`
            )}</div>`,
            admission: `<div${index > 0 ? ' style="margin-top: .5rem;"' : ""}>${iterationLabel}${mlang(
                `Einlass ab ${admission.locale("de").format("HH:mm [Uhr]")} (${entryOffset} Minuten vor Prüfungsbeginn)`,
                `Admission from ${admission.locale("en-gb").format("HH:mm")} (${entryOffset} minutes before the exam begins)`
            )}</div>`,
        }
    })
    const scheduledExamWindow = iterationSchedule
        .map(iteration => iteration.examWindow)
        .join("")
    const scheduledEntryTime = iterationSchedule
        .map(iteration => iteration.admission)
        .join("")
    const configuredDuration = `${duration} ${mlang("Minuten", "minutes")}`
    const selectedExamLanguage =
        examLanguageOptions.find(option => option.key === examLanguage) ??
        examLanguageOptions[0]
    const examLanguageContent = mlang(
        selectedExamLanguage.de,
        selectedExamLanguage.en
    )
    const sharedPlatformContent = (
        content: string,
        overrides: Partial<Record<PlatformKey, string>> = {}
    ): Record<PlatformKey, string> => ({
        computerLab: overrides.computerLab ?? content,
        personalDevice: overrides.personalDevice ?? content,
        smowl: overrides.smowl ?? content,
        examOnDemand: overrides.examOnDemand ?? content,
    })
    const selectedAidsContent = (category: AidCategory) => {
        const selectedOptions = aidOptions.filter(option =>
            option.category === category &&
            selectedAidKeys.includes(option.key)
        )

        if (!selectedOptions.length) {
            return category === "digital"
                ? mlang(
                    "Für diese Prüfung sind keine digitalen Hilfsmittel zugelassen.",
                    "No digital aids are permitted for this examination."
                )
                : mlang(
                    "Für diese Prüfung sind keine nicht digitalen Hilfsmittel zugelassen.",
                    "No non-digital aids are permitted for this examination."
                )
        }

        return `<ul class="mb-0">${selectedOptions
            .map(option => `<li>${option.content}</li>`)
            .join("")}</ul>`
    }
    const digitalAidsContent = selectedAidsContent("digital")
    const nonDigitalAidsContent = selectedAidsContent("nonDigital")
    const copyAndPasteContent = selectedAidKeys.some(key =>
        aidOptions.some(option =>
            option.key === key && option.category === "digital"
        )
    )
        ? mlang(
            "Kopieren und Einfügen ist zwischen der Prüfung und den oben aufgeführten digitalen Hilfsmitteln gestattet.",
            "Copying and pasting is permitted between the examination and the digital aids listed above."
        )
        : mlang(
            "Kopieren und Einfügen aus externen Webseiten oder Anwendungen ist nicht gestattet.",
            "Copying and pasting from external websites or applications is not permitted."
        )

    const renderRowCells = (row: {
        computerLab: string
        personalDevice: string
        smowl: string
        examOnDemand: string
    }) => {
        const groups: Array<{
            platforms: PlatformOption[]
            content: string
            restricted: boolean
        }> = []

        for (const platform of columns) {
            const content = row[platform.key]
            const restricted =
                platform.key === "smowl" && restrictSmowlToChoiceGroup
            const previous = groups[groups.length - 1]

            if (
                previous &&
                previous.content === content &&
                previous.restricted === restricted
            ) {
                previous.platforms.push(platform)
            } else {
                groups.push({
                    platforms: [platform],
                    content,
                    restricted,
                })
            }
        }

        return groups
            .map(group => {
                const renderedCell = cell(
                    group.content,
                    group.platforms.length
                )

                return group.restricted
                    ? `{ifingroup choice_smowl}${renderedCell}{/ifingroup}`
                    : renderedCell
            })
            .join("")
    }

    const rows: GuidelineRow[] = [
        {
            category: "aids",
            label: mlang("Digitale Hilfsmittel", "Digital Aids"),
            ...sharedPlatformContent(digitalAidsContent),
        },
        {
            category: "aids",
            label: mlang("Zusätzliche Hardware", "Additional Hardware"),
            computerLab: mlang("Zusätzliche oder eigene Hardware darf nur nach vorheriger Genehmigung verwendet werden.", "Additional or personal hardware may only be used with prior approval."),
            personalDevice: mlang("Bei Bedarf dürfen Texteingabegeräte, elektronische Stifte und Zeigegeräte mit dem mobilen Computer verwendet werden.", "Text input devices, electronic pens and pointing devices may be used with the mobile computer as needed."),
            smowl: mlang("Bildschirme, Eingabegeräte und das Smartgerät sind entsprechend den Vorgaben einzurichten. Ohrhörer und Kopfhörer sind nicht zulässig.", "Displays, input devices and the smart device must be set up in accordance with the instructions. Earphones and headphones are not permitted."),
            examOnDemand: mlang("Zusätzliche oder eigene Hardware darf nur nach vorheriger Genehmigung verwendet werden.", "Additional or personal hardware may only be used with prior approval."),
        },
        {
            category: "aids",
            label: mlang("Nicht digitale Hilfsmittel", "Non-digital Aids"),
            ...sharedPlatformContent(nonDigitalAidsContent),
        },
        {
            category: "aids",
            label: mlang("Kopieren und Einfügen", "Copy and Paste"),
            ...sharedPlatformContent(copyAndPasteContent),
        },
        {
            category: "conduct",
            label: mlang("Prüfungssprache", "Exam Language"),
            ...sharedPlatformContent(examLanguageContent),
        },
        {
            category: "technical",
            label: mlang("Erforderliche Hardware", "Required Hardware"),
            computerLab: mlang("Ein eigener Computer ist nicht erforderlich. Für die Zwei-Faktor-Authentifizierung ist ein korrekt eingerichteter 2FA-TOTP-Token erforderlich, beispielsweise auf einem Smartphone, Hardware-Token oder geeigneten Zweitgerät.", "A personal computer is not required. A correctly configured 2FA TOTP token is required for two-factor authentication, for example on a smartphone, hardware token or suitable secondary device."),
            personalDevice: mlang("Erforderlich sind ein Notebook oder Tablet mit Bildschirm, WLAN und Tastatur sowie ein korrekt eingerichteter 2FA-TOTP-Token, beispielsweise auf einem Smartphone, Hardware-Token oder geeigneten Zweitgerät.", "A notebook or tablet with a display, Wi-Fi and keyboard is required, as well as a correctly configured 2FA TOTP token, for example on a smartphone, hardware token or suitable secondary device."),
            smowl: mlang("Erforderlich sind ein Computer mit Webcam und Mikrofon sowie ein Smartgerät mit Kamera zur Überwachung des Arbeitsplatzes. Für die Zwei-Faktor-Authentifizierung ist ein korrekt eingerichteter 2FA-TOTP-, 2FA-WebAuthn- oder 2FA-HOTP-Token erforderlich.", "A computer with a webcam and microphone is required, as well as a camera-equipped smart device for monitoring the workspace. A correctly configured 2FA TOTP, 2FA WebAuthn or 2FA HOTP token is required for two-factor authentication."),
            examOnDemand: mlang("Ein eigener Computer ist nicht erforderlich. Für die Zwei-Faktor-Authentifizierung ist ein korrekt eingerichteter 2FA-TOTP-Token erforderlich, beispielsweise auf einem Smartphone, Hardware-Token oder geeigneten Zweitgerät.", "A personal computer is not required. A correctly configured 2FA TOTP token is required for two-factor authentication, for example on a smartphone, hardware token or suitable secondary device."),
        },
        {
            category: "technical",
            label: mlang("Anmeldung und Authentifizierung", "Login and Authentication"),
            ...sharedPlatformContent(
                mlang(
                    "Stellen Sie vor Prüfungsbeginn sicher, dass Ihnen Ihre TU-ID und das korrekte Passwort vorliegen, das Ihnen vertraute Tastaturlayout eingestellt ist und Ihr 2FA-TOTP-Token ordnungsgemäß eingerichtet und funktionsfähig ist. Die Beschriftung der angeschlossenen Tastatur ist für die Auswahl des Tastaturlayouts unerheblich. Für selbst zu vertretende Anmeldeverzögerungen besteht grundsätzlich kein Anspruch auf zusätzliche Bearbeitungszeit. Kann die Prüfung deshalb nicht ordnungsgemäß begonnen werden, kann dies nach Maßgabe der geltenden Prüfungsbestimmungen als Fehlversuch gewertet werden.",
                    "Before the exam begins, ensure that you have your TU ID and correct password, that your preferred keyboard layout is selected and that your 2FA TOTP token is properly configured and operational. Select the layout you are most familiar with, regardless of the labels printed on the connected keyboard. There is generally no entitlement to additional working time for login delays for which the candidate is responsible. If the exam cannot be started correctly as a result, it may be treated as a failed attempt in accordance with the applicable examination regulations."
                ),
                {
                    smowl: mlang(
                        "Stellen Sie vor Prüfungsbeginn sicher, dass Ihnen Ihre TU-ID und das korrekte Passwort vorliegen, das Ihnen vertraute Tastaturlayout eingestellt ist und Ihr 2FA-TOTP-, 2FA-WebAuthn- oder 2FA-HOTP-Token ordnungsgemäß eingerichtet und funktionsfähig ist. Die Beschriftung der angeschlossenen Tastatur ist für die Auswahl des Tastaturlayouts unerheblich. Für selbst zu vertretende Anmeldeverzögerungen besteht grundsätzlich kein Anspruch auf zusätzliche Bearbeitungszeit. Kann die Prüfung deshalb nicht ordnungsgemäß begonnen werden, kann dies nach Maßgabe der geltenden Prüfungsbestimmungen als Fehlversuch gewertet werden.",
                        "Before the exam begins, ensure that you have your TU ID and correct password, that your preferred keyboard layout is selected and that your 2FA TOTP, 2FA WebAuthn or 2FA HOTP token is properly configured and operational. Select the layout you are most familiar with, regardless of the labels printed on the connected keyboard. There is generally no entitlement to additional working time for login delays for which the candidate is responsible. If the exam cannot be started correctly as a result, it may be treated as a failed attempt in accordance with the applicable examination regulations."
                    ),
                }
            ),
        },
        {
            category: "technical",
            label: mlang("Erforderliche Software", "Required Software"),
            computerLab: mlang("Die erforderliche Prüfungssoftware einschließlich Safe Exam Browser ist vor Ort installiert und eingerichtet.", "The required examination software, including Safe Exam Browser, is installed and configured on site."),
            personalDevice: mlang("Safe Exam Browser muss vorab in der aktuellen Version installiert und erfolgreich getestet werden.", "The latest version of Safe Exam Browser must be installed and tested successfully in advance."),
            smowl: mlang("SMOWL CM und ein unterstützter Browser müssen vorab installiert und erfolgreich getestet werden.", "SMOWL CM and a supported browser must be installed and tested successfully in advance."),
            examOnDemand: mlang("Die erforderliche Prüfungssoftware einschließlich Safe Exam Browser ist vor Ort installiert und eingerichtet.", "The required examination software, including Safe Exam Browser, is installed and configured on site."),
        },
        {
            category: "technical",
            label: mlang("Testläufe und Voraussetzungen", "Test Runs and Prerequisites"),
            computerLab: mlang("Die Teilnahme an einer Demoprüfung mit Safe Exam Browser wird empfohlen.", "Participation in a Safe Exam Browser demo exam is recommended."),
            personalDevice: mlang("Das vorgeschriebene Tutorial beziehungsweise der Testlauf muss mit der aktuellen Version von Safe Exam Browser erfolgreich abgeschlossen werden.", "The mandatory tutorial or test run must be completed successfully using the latest version of Safe Exam Browser."),
            smowl: mlang("Der vorgeschriebene Testlauf muss mit der aktuellen Version von SMOWL CM und einem Smartgerät als Arbeitsplatzkamera erfolgreich abgeschlossen werden.", "The mandatory test run must be completed successfully using the latest version of SMOWL CM and a smart device as the workspace camera."),
            examOnDemand: mlang("Optional kann eine Demoprüfung mit einem frei wählbaren Termin gebucht werden.", "An optional demo exam may be booked for an available appointment."),
        },
        {
            category: "conduct",
            label: mlang("Toilettenregelung", "Restroom Policy"),
            computerLab: mlang("Melden Sie sich bei der Aufsicht und warten Sie deren Anweisungen ab.", "Notify a supervisor and wait for further instructions."),
            personalDevice: mlang("Melden Sie sich bei der Aufsicht und warten Sie deren Anweisungen ab.", "Notify a supervisor and wait for further instructions."),
            smowl: mlang("Melden Sie den Toilettengang über das Zoom-Meeting bei der Aufsicht an, warten Sie die Freigabe ab und melden Sie sich nach der Rückkehr erneut.", "Request permission from the supervisor via the Zoom meeting, wait for approval and report back after returning."),
            examOnDemand: mlang("Toilettengänge sind gemäß dem im Prüfungsraum vorgesehenen Ablauf und den dort kommunizierten Anweisungen möglich.", "Restroom breaks are permitted in accordance with the procedure established for the examination room and the instructions provided on site."),
        },
        {
            category: "conduct",
            label: mlang("Fragen zu Prüfungsinhalten", "Questions About Exam Content"),
            ...(selectedPlatforms.includes("examOnDemand")
                ? sharedPlatformContent(mlang(
                    "Während der Prüfung können keine inhaltlichen Rückfragen beantwortet werden.",
                    "Questions concerning the exam content cannot be answered during the exam."
                ))
                : {
                    computerLab: mlang("Richten Sie inhaltliche Fragen entsprechend den bekannt gegebenen Anweisungen an die Aufsicht.", "Direct questions concerning the exam content to the supervisor in accordance with the announced instructions."),
                    personalDevice: mlang("Richten Sie inhaltliche Fragen entsprechend den bekannt gegebenen Anweisungen an die Aufsicht.", "Direct questions concerning the exam content to the supervisor in accordance with the announced instructions."),
                    smowl: mlang("Richten Sie inhaltliche Fragen über das Zoom-Meeting an die Aufsicht.", "Direct questions concerning the exam content to the supervisor via the Zoom meeting."),
                    examOnDemand: mlang("Während der Prüfung können keine inhaltlichen Rückfragen beantwortet werden.", "Questions concerning the exam content cannot be answered during the exam."),
                }),
        },
        {
            category: "conduct",
            label: mlang("Hilfe bei technischen Problemen", "Help with Technical Issues"),
            computerLab: mlang("Melden Sie technische Probleme unverzüglich der Aufsicht oder dem technischen Personal und folgen Sie den weiteren Anweisungen.", "Report technical issues to the supervisor or technical staff immediately and follow the instructions provided."),
            personalDevice: mlang("Melden Sie technische Probleme unverzüglich der Aufsicht oder dem technischen Personal und folgen Sie den weiteren Anweisungen.", "Report technical issues to the supervisor or technical staff immediately and follow the instructions provided."),
            smowl: mlang("Melden Sie technische Probleme unverzüglich über das Zoom-Meeting bei der Aufsicht. Bei Bedarf wird technisches Personal hinzugezogen.", "Report technical issues to the supervisor immediately via the Zoom meeting. Technical staff will be involved if required."),
            examOnDemand: mlang("Verlassen Sie bei einem technischen Problem den Prüfungsraum und lassen Sie den Vorfall am Servicepoint prüfen. Wird der Vorfall als technischer Abbruch anerkannt, kann ein neuer Termin gebucht werden.", "If a technical issue occurs, leave the exam room and have the incident reviewed at the service point. If it is recognised as a technical interruption, a new appointment may be booked."),
        },
        {
            category: "conduct",
            label: mlang("Getränke und Speisen", "Drinks and Food"),
            computerLab: mlang("Es gelten die jeweiligen Hausregeln. Wasser ist grundsätzlich erlaubt.", "The applicable house rules must be observed. Water is generally permitted."),
            personalDevice: mlang("Es gelten die jeweiligen Hausregeln. Wasser ist grundsätzlich erlaubt.", "The applicable house rules must be observed. Water is generally permitted."),
            smowl: mlang("Getränke und Speisen sind grundsätzlich erlaubt.", "Drinks and food are generally permitted."),
            examOnDemand: mlang("Getränkeflaschen sind in den dafür vorgesehenen Halterungen zu verstauen.", "Beverage bottles must be placed in the designated holders."),
        },
        {
            category: "conduct",
            label: mlang("Identitätsprüfung / Beaufsichtigung", "Identity Verification / Supervision"),
            computerLab: mlang("Bringen Sie einen gültigen amtlichen Lichtbildausweis mit und legen Sie ihn vor Prüfungsbeginn gut sichtbar neben sich bereit, damit die Aufsicht die Identitätskontrolle durchführen kann, ohne Sie dabei unnötig zu stören.", "Bring a valid official photo ID and place it clearly visible beside you before the exam begins so that the supervisor can verify your identity without unnecessarily disturbing you."),
            personalDevice: mlang("Bringen Sie einen gültigen amtlichen Lichtbildausweis mit und legen Sie ihn vor Prüfungsbeginn gut sichtbar neben sich bereit, damit die Aufsicht die Identitätskontrolle durchführen kann, ohne Sie dabei unnötig zu stören.", "Bring a valid official photo ID and place it clearly visible beside you before the exam begins so that the supervisor can verify your identity without unnecessarily disturbing you."),
            smowl: mlang("Die Identitätsprüfung erfolgt über SMOWL durch den Abgleich des Ausweisdokuments mit den erfassten Aufnahmen.", "Identity is verified through SMOWL by comparing the identification document with the captured images."),
            examOnDemand: mlang("Die Identitätsprüfung erfolgt vor Prüfungsbeginn am Servicepoint.", "Identity is verified at the service point before the exam begins."),
        },
        {
            category: "conduct",
            label: mlang("Prüfungsende", "End of Exam"),
            computerLab: mlang("Nach erfolgreicher Abgabe dürfen Sie den Prüfungsraum verlassen.", "You may leave the examination room after submitting your exam successfully."),
            personalDevice: mlang("Nach erfolgreicher Abgabe dürfen Sie den Prüfungsraum verlassen.", "You may leave the examination room after submitting your exam successfully."),
            examOnDemand: mlang("Nach erfolgreicher Abgabe dürfen Sie den Prüfungsraum verlassen.", "You may leave the examination room after submitting your exam successfully."),
            smowl: mlang("Mit der erfolgreichen Abgabe ist der Prüfungsversuch beendet. Anschließend können Sie SMOWL und das Zoom-Meeting schließen.", "The exam attempt ends once it has been submitted successfully. You may then close SMOWL and leave the Zoom meeting."),
        },
    ]

    const hasScheduledExam = columns.some(
        column => column.key !== "examOnDemand"
    )
    const hasScheduledOnSiteExam = columns.some(column =>
        column.key === "computerLab" || column.key === "personalDevice"
    )
    const hasExamOnDemand = columns.some(
        column => column.key === "examOnDemand"
    )
    const scheduleRows: Array<{label: string; content: string}> = []

    if (hasScheduledExam) {
        scheduleRows.push({
            label: mlang("Prüfungstermine", "Exam Schedule"),
            content: scheduledExamWindow,
        })
    }

    if (hasExamOnDemand) {
        scheduleRows.push({
            label: mlang("Prüfung On Demand", "Exam On Demand"),
            content: bookedExamWindow,
        })
    }

    scheduleRows.push({
        label: mlang("Bearbeitungsdauer", "Duration"),
        content: configuredDuration,
    })

    if (hasScheduledOnSiteExam) {
        scheduleRows.push({
            label: mlang("Einlass", "Admission"),
            content: scheduledEntryTime,
        })
    }

    const scheduleHtml = `<p style="font-size: 1.2rem;"><strong>${mlang(
        "Termine und Ablauf",
        "Schedule and Procedure"
    )}</strong></p><table class="table table-bordered" style="width: 100%;"><tbody>${scheduleRows
        .map(row => `<tr><th style="width: 26%; padding: .75rem; vertical-align: top;">${row.label}</th><td style="padding: .75rem; vertical-align: top;">${row.content}</td></tr>`)
        .join("")}</tbody></table>`

    const categoryLabels: Record<GuidelineCategory, string> = {
        aids: mlang("Hilfsmittel", "Aids"),
        technical: mlang("Technische Voraussetzungen", "Technical Requirements"),
        conduct: mlang("Ablauf und Verhalten", "Procedure and Conduct"),
    }
    const guidelinesTablesHtml = ([
        "aids",
        "technical",
        "conduct",
    ] as GuidelineCategory[])
        .map(category => {
            const categoryRows = rows.filter(
                row => row.category === category
            )

            if (!categoryRows.length) {
                return ""
            }

            return `<p style="font-size: 1.2rem;"><strong>${categoryLabels[category]}</strong></p><table class="table table-bordered table-striped" style="width: 100%;"><thead><tr><th style="width: 26%; padding: .75rem;"></th>${columns.map(headerCell).join("")}</tr></thead><tbody>${categoryRows
                .map(row => `<tr><th style="padding: .75rem; vertical-align: top;">${row.label}</th>${renderRowCells(row)}</tr>`)
                .join("")}</tbody></table>`
        })
        .join("")

    const platformSpecificHeading = rows.length
        ? `<p style="font-size: 1.2rem;"><strong>${mlang(
            "Plattformspezifische Vorgaben",
            "Platform-specific Requirements"
        )}</strong></p>`
        : ""

    return `<p style="font-size: 1.40625rem;"><strong>${mlang(
        "Prüfungsrichtlinien",
        "Examination Guidelines"
    )}</strong></p>${scheduleHtml}${platformSpecificHeading}${guidelinesTablesHtml}`
}

const ExamPasterComponent: FC = () => {
    const {t, i18n} = useTranslation("admin")
    const [data, setData] = useState<Building[]>([])
    const [examDate, setExamDate] = useState("2026-06-30")
    const [arrivalMinutes, setArrivalMinutes] = useState(20)
    const [examDurationMinutes, setExamDurationMinutes] = useState(90)
    const [lecturerEmail, setLecturerEmail] = useState("")
    const [copied, setCopied] = useState(false)
    const [guidelinesCopied, setGuidelinesCopied] = useState(false)
    const [enrollmentEmailCopied, setEnrollmentEmailCopied] = useState(false)
    const [restrictSmowlToChoiceGroup, setRestrictSmowlToChoiceGroup] = useState(false)
    const [examLanguage, setExamLanguage] = useState<ExamLanguageKey>("german")
    const [previewLanguage, setPreviewLanguage] = useState<PreviewLanguage>("de")
    const [previewRole, setPreviewRole] = useState<PreviewRole>("student")
    const [previewRegistered, setPreviewRegistered] = useState(true)
    const [previewRoomAssigned, setPreviewRoomAssigned] = useState(false)
    const [previewRoom, setPreviewRoom] = useState("")
    const [previewTimeSlot, setPreviewTimeSlot] = useState("")
    const [previewChoiceSmowl, setPreviewChoiceSmowl] = useState(false)
    const [selectedAidKeys, setSelectedAidKeys] = useState<AidKey[]>([])

    const [selectedPlatforms, setSelectedPlatforms] = useState<PlatformKey[]>([
        "computerLab",
    ])

    const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([
        { group: "iter1", time: "13:30" },
        { group: "iter2", time: "15:20" },
    ])

    useEffect(() => {
        axios.get("/api/site/list").then(result => setData(result.data))
    }, [])

    const availableRooms = useMemo(() => {
        return rooms
            .map(room => {
                const buildingIdentifier = getBuildingIdentifierFromRoom(room)
                const building = data.find(entry => entry.identifier === buildingIdentifier)

                if (!building) {
                    return null
                }

                return {
                    building,
                    label: room,
                }
            })
            .filter((entry): entry is { building: Building; label: string } => entry !== null)
    }, [data])

    const usedBuildings = useMemo(() => {
        return data.filter(building =>
            rooms.some(room => getBuildingIdentifierFromRoom(room) === building.identifier)
        )
    }, [data])

    const groupImportCsv = useMemo(() => {
        const rows = [
            "groupname;groupidnumber",
            ...timeSlots.map((slot, index) => {
                const start = moment(`${examDate} ${slot.time}`, "YYYY-MM-DD HH:mm")
                const end = start.clone().add(examDurationMinutes, "minutes")

                return `Iteration ${index + 1} - ${start.format("HH:mm")} until ${end.format("HH:mm")};${slot.group}`
            }),
        ]

        return rows.join("\n")
    }, [examDate, examDurationMinutes, timeSlots])

    const enrollmentEmailHtml = useMemo(() => {
        const courseUrl =
            "https://moodle-exam.tu-darmstadt.de/course/view.php?id={courseid}"
        const demoCourseUrl =
            "https://moodle-exam.tu-darmstadt.de/course/view.php?id=9"
        const date = moment(examDate, "YYYY-MM-DD", true)
        const germanDate = date.locale("de").format("D. MMMM YYYY")
        const englishDate = date.locale("en-gb").format("D MMMM YYYY")
        const times = timeSlots.map(slot => slot.time)
        const joinList = (items: string[], conjunction: string) => {
            if (items.length < 2) {
                return items[0] ?? ""
            }

            return `${items.slice(0, -1).join(", ")} ${conjunction} ${items[items.length - 1]}`
        }
        const germanSchedule = times.length === 1
            ? `am ${germanDate} um ${times[0]} Uhr`
            : `am ${germanDate} in einem der Zeitfenster ${joinList(times, "oder")} Uhr`
        const englishSchedule = times.length === 1
            ? `on ${englishDate} at ${times[0]}`
            : `on ${englishDate} in one of the time slots ${joinList(times, "or")}`
        const safeLecturerEmail = escapeHtml(lecturerEmail.trim())
        const germanLecturerContact = safeLecturerEmail
            ? `wenden Sie sich bitte an <a href="mailto:${safeLecturerEmail}">${safeLecturerEmail}</a>`
            : "wenden Sie sich bitte an die Lehrenden der Lehrveranstaltung"
        const englishLecturerContact = safeLecturerEmail
            ? `please contact <a href="mailto:${safeLecturerEmail}">${safeLecturerEmail}</a>`
            : "please contact the teaching staff responsible for the course"
        const now = moment()
        const currentMonth = now.month() + 1
        const currentYear = now.year()
        const isSummerTerm = currentMonth >= 4 && currentMonth <= 9
        const winterStartYear = currentMonth >= 10
            ? currentYear
            : currentYear - 1
        const germanTerm = isSummerTerm
            ? `Sommersemester ${currentYear}`
            : `Wintersemester ${String(winterStartYear).slice(-2)}/${String(winterStartYear + 1).slice(-2)}`
        const englishTerm = isSummerTerm
            ? `summer term ${currentYear}`
            : `winter term ${String(winterStartYear).slice(-2)}/${String(winterStartYear + 1).slice(-2)}`
        const generallyAvailablePlatforms =
            restrictSmowlToChoiceGroup &&
            selectedPlatforms.includes("smowl")
                ? selectedPlatforms.filter(platform => platform !== "smowl")
                : selectedPlatforms
        const germanPlatformSelectionNotice =
            generallyAvailablePlatforms.length > 1
                ? " Dort werden außerdem die verfügbaren Prüfungsplattformen kommuniziert und können innerhalb der bekannt gegebenen Frist ausgewählt werden."
                : ""
        const englishPlatformSelectionNotice =
            generallyAvailablePlatforms.length > 1
                ? " The available examination platforms are also communicated there and can be selected within the announced deadline."
                : ""

        return `<p><em>English text below</em></p>

<p>Sehr geehrte/r {$a->fullname},</p>

<p>Sie wurden soeben in den Moodle-Kurs zur E-Prüfung <strong>{$a->coursename}</strong> für das ${germanTerm} eingeschrieben. Den Prüfungskurs erreichen Sie unter <a href="${courseUrl}">${courseUrl}</a>.</p>

<p>Die Prüfung findet ${germanSchedule} statt und hat eine Bearbeitungszeit von ${examDurationMinutes} Minuten.</p>

<p>Auf der Kursübersicht finden Sie Informationen zu den Prüfungsregelungen und zur Vorbereitung auf E-Prüfungen in Moodle. Spätestens vier Werktage vor der Prüfung können Sie dort Ihre Raumeinteilung einsehen.${germanPlatformSelectionNotice} Lesen Sie die dort veröffentlichten Prüfungsrichtlinien einschließlich der zugelassenen Hilfsmittel vor dem Prüfungstermin sorgfältig durch. Zusätzlich können Sie im <a href="${demoCourseUrl}">Kurs mit fachübergreifenden Demoprüfungen</a> den Ablauf und das digitale Prüfungsformat kennenlernen.</p>

<p>Bei technischen Problemen oder Fragen zu Moodle und dem E-Prüfungsformat wenden Sie sich bitte an <a href="mailto:e-pruefungen@tu-darmstadt.de">e-pruefungen@tu-darmstadt.de</a>. Bei Fragen zur Prüfungsorganisation oder zu den Prüfungsinhalten ${germanLecturerContact}.</p>

<hr>

<p>Dear {$a->fullname},</p>

<p>You have just been enrolled in the Moodle course for the e-examination <strong>{$a->coursename}</strong> for the ${englishTerm}. You can access the examination course at <a href="${courseUrl}">${courseUrl}</a>.</p>

<p>The examination will take place ${englishSchedule} and will have a working time of ${examDurationMinutes} minutes.</p>

<p>The course overview contains information about the examination rules and preparation for e-examinations in Moodle. Your room assignment will be available there no later than four working days before the examination.${englishPlatformSelectionNotice} Read the published examination guidelines, including the permitted aids, carefully before the examination date. You can also use the <a href="${demoCourseUrl}">course containing interdisciplinary demo examinations</a> to become familiar with the procedure and the digital examination format.</p>

<p>If you experience technical problems or have questions about Moodle or the e-examination format, please contact <a href="mailto:e-pruefungen@tu-darmstadt.de">e-pruefungen@tu-darmstadt.de</a>. For questions concerning examination organisation or content, ${englishLecturerContact}.</p>`
    }, [
        examDate,
        examDurationMinutes,
        lecturerEmail,
        restrictSmowlToChoiceGroup,
        selectedPlatforms,
        timeSlots,
    ])

    const guidelinesHtml = useMemo(() => {
        return renderGuidelinesTable(
            selectedPlatforms,
            restrictSmowlToChoiceGroup,
            arrivalMinutes,
            examLanguage,
            examDate,
            examDurationMinutes,
            timeSlots,
            selectedAidKeys
        )
    }, [
        arrivalMinutes,
        examDate,
        examDurationMinutes,
        examLanguage,
        restrictSmowlToChoiceGroup,
        selectedAidKeys,
        selectedPlatforms,
        timeSlots,
    ])

    const generatedHtml = useMemo(() => {
        const exclusiveSmowl =
            selectedPlatforms.includes("smowl") &&
            restrictSmowlToChoiceGroup
        const regularPlatforms = exclusiveSmowl
            ? selectedPlatforms.filter(platform => platform !== "smowl")
            : selectedPlatforms
        const hasTimeSlotChoice = timeSlots.length > 1
        const hasPlatformChoice = regularPlatforms.length > 1
        const selectionCard = (title: string, content: string) =>
            `<div class="alert alert-info" role="alert"><p><strong>🖥️ ${title}</strong></p><p class="mb-0">${content}</p></div>`
        const onlyPlatform = regularPlatforms[0]
        const noSelectionContent: Record<PlatformKey, string> = {
            computerLab: mlang(
                "Die Prüfung findet ausschließlich an von der TU Darmstadt bereitgestellten Rechnern in Computerpoolräumen statt. Daher ist keine Auswahl über das bevorzugte Endgerät erforderlich. Solltest du aus besonderen Gründen eine Ausnahme benötigen, wende dich bitte frühzeitig mit einer entsprechenden Begründung an die Lehrenden.",
                "This examination will take place exclusively on computers provided by TU Darmstadt in computer labs. Therefore, no selection of a preferred device is required. If you require an exception due to special circumstances, please contact the teaching staff as early as possible and provide an appropriate justification."
            ),
            personalDevice: mlang(
                "Die Prüfung findet ausschließlich vor Ort auf dem eigenen Gerät statt. Daher ist keine Auswahl der Prüfungsplattform erforderlich. Solltest du aus besonderen Gründen eine Ausnahme benötigen, wende dich bitte frühzeitig mit einer entsprechenden Begründung an die Lehrenden.",
                "This examination will take place exclusively on site using your personal device. Therefore, no selection of an examination platform is required. If you require an exception due to special circumstances, please contact the teaching staff as early as possible and provide an appropriate justification."
            ),
            smowl: mlang(
                "Die Prüfung findet ausschließlich per Fernaufsicht mit SMOWL statt. Daher ist keine Auswahl der Prüfungsplattform erforderlich. Beachte bitte die Hinweise zur Vorbereitung und Beaufsichtigung in den Prüfungsrichtlinien.",
                "This examination will take place exclusively under remote proctoring with SMOWL. Therefore, no selection of an examination platform is required. Please follow the preparation and proctoring information provided in the examination guidelines."
            ),
            examOnDemand: mlang(
                "Die Prüfung findet ausschließlich als Prüfung On Demand statt. Daher ist keine Auswahl der Prüfungsplattform erforderlich.",
                "This examination will take place exclusively as an Exam On Demand. Therefore, no selection of an examination platform is required."
            ),
        }
        const choiceDetails = [
            hasPlatformChoice
                ? mlang(
                    "Wähle innerhalb der bekannt gegebenen Frist die gewünschte Prüfungsplattform.",
                    "Select your preferred examination platform within the announced deadline."
                )
                : "",
            hasTimeSlotChoice
                ? mlang(
                    "Wähle innerhalb der bekannt gegebenen Frist dein bevorzugtes Zeitfenster. Wenn du die Frist nicht wahrnimmst, wirst du zufallsbasiert einer der verfügbaren Iterationen zugeteilt.",
                    "Select your preferred time slot within the announced deadline. If you miss the deadline, you will be assigned randomly to one of the available iterations."
                )
                : "",
        ].filter(Boolean).join(" ")
        const choiceTitle = hasPlatformChoice && hasTimeSlotChoice
            ? mlang(
                "Für diese Prüfung ist eine Wahl der Prüfungsplattform und des Zeitfensters vorgesehen.",
                "A selection of the examination platform and time slot is provided for this examination."
            )
            : hasPlatformChoice
                ? mlang(
                    "Für diese Prüfung ist eine Wahl der Prüfungsplattform vorgesehen.",
                    "A selection of the examination platform is provided for this examination."
                )
                : mlang(
                    "Für diese Prüfung ist eine Wahl des Zeitfensters vorgesehen.",
                    "A selection of the time slot is provided for this examination."
                )
        const regularSelectionInformationHtml =
            hasPlatformChoice || hasTimeSlotChoice
                ? selectionCard(choiceTitle, choiceDetails)
                : onlyPlatform
                    ? selectionCard(
                        mlang(
                            "Für diese Prüfung ist kein Wahlverfahren vorgesehen.",
                            "No selection process is provided for this examination."
                        ),
                        noSelectionContent[onlyPlatform]
                    )
                    : ""
        const smowlSelectionInformationHtml = selectionCard(
            mlang(
                "Für dich ist die Teilnahme per Fernaufsicht mit SMOWL vorgesehen.",
                "You will take the examination remotely under SMOWL proctoring."
            ),
            hasTimeSlotChoice
                ? mlang(
                    "Für dich ist keine Raumeinteilung erforderlich. Wähle innerhalb der bekannt gegebenen Frist dein bevorzugtes Zeitfenster. Wenn du die Frist nicht wahrnimmst, wirst du zufallsbasiert einer der verfügbaren Iterationen zugeteilt. Stelle sicher, dass du das aktuelle Badge im <a href=\"https://moodle-exam.tu-darmstadt.de/course/section.php?id=5292\" target=\"_blank\" rel=\"noopener\">SMOWL-Vorbereitungsbereich</a> erworben hast. Das Badge ist Voraussetzung für die Teilnahme. Ob das Badge ausgestellt wurde, kannst du unter <a href=\"https://moodle-exam.tu-darmstadt.de/badges/mybadges.php\" target=\"_blank\" rel=\"noopener\"><strong>Meine Badges</strong></a> prüfen. Beachte bitte außerdem die Hinweise zur Vorbereitung und Beaufsichtigung in den Prüfungsrichtlinien.",
                    "No room assignment is required for you. Select your preferred time slot within the announced deadline. If you miss the deadline, you will be assigned randomly to one of the available iterations. Make sure that you have earned the current badge in the <a href=\"https://moodle-exam.tu-darmstadt.de/course/section.php?id=5292\" target=\"_blank\" rel=\"noopener\">SMOWL preparation section</a>. The badge is required to participate. You can verify that the badge has been issued under <a href=\"https://moodle-exam.tu-darmstadt.de/badges/mybadges.php\" target=\"_blank\" rel=\"noopener\"><strong>My badges</strong></a>. Please also follow the preparation and proctoring information provided in the examination guidelines."
                )
                : mlang(
                    "Für dich ist keine Raumeinteilung erforderlich. Stelle sicher, dass du das aktuelle Badge im <a href=\"https://moodle-exam.tu-darmstadt.de/course/section.php?id=5292\" target=\"_blank\" rel=\"noopener\">SMOWL-Vorbereitungsbereich</a> erworben hast. Das Badge ist Voraussetzung für die Teilnahme. Ob das Badge ausgestellt wurde, kannst du unter <a href=\"https://moodle-exam.tu-darmstadt.de/badges/mybadges.php\" target=\"_blank\" rel=\"noopener\"><strong>Meine Badges</strong></a> prüfen. Beachte bitte außerdem die Hinweise zur Vorbereitung und Beaufsichtigung in den Prüfungsrichtlinien.",
                    "No room assignment is required for you. Make sure that you have earned the current badge in the <a href=\"https://moodle-exam.tu-darmstadt.de/course/section.php?id=5292\" target=\"_blank\" rel=\"noopener\">SMOWL preparation section</a>. The badge is required to participate. You can verify that the badge has been issued under <a href=\"https://moodle-exam.tu-darmstadt.de/badges/mybadges.php\" target=\"_blank\" rel=\"noopener\"><strong>My badges</strong></a>. Please also follow the preparation and proctoring information provided in the examination guidelines."
                )
        )
        const selectionInformationHtml = exclusiveSmowl
            ? `{ifnotingroup choice_smowl}${regularSelectionInformationHtml}{/ifnotingroup}

{ifingroup choice_smowl}${smowlSelectionInformationHtml}{/ifingroup}`
            : regularSelectionInformationHtml
        const regularArrivalInstruction = mlang(
            `Befinde dich spätestens <span class="text-primary"><strong>${arrivalMinutes} Minuten vor der Prüfung</strong></span> vor dem Raum.`,
            `Be in front of the room at least <span class="text-primary"><strong>${arrivalMinutes} minutes before the exam</strong></span>.`
        )
        const smowlArrivalInstruction = mlang(
            "Halte dich zum angegebenen Prüfungsbeginn vollständig startbereit.",
            "Be fully prepared to start at the stated exam time."
        )
        const arrivalInstruction = exclusiveSmowl
            ? `{ifnotingroup choice_smowl}${regularArrivalInstruction}{/ifnotingroup}{ifingroup choice_smowl}${smowlArrivalInstruction}{/ifingroup}`
            : regularArrivalInstruction
        const regularAuthenticationRequirement = mlang(
            "Nach dem Einlass in den Prüfungsraum darfst du dich direkt einloggen. Halte dafür deine <strong>TU-ID</strong>, dein <strong>korrektes Passwort</strong> und einen ordnungsgemäß eingerichteten <strong>2FA-TOTP-Token</strong> bereit.",
            "After entering the examination room, you may log in immediately. Have your <strong>TU ID</strong>, <strong>correct password</strong> and a properly configured <strong>2FA TOTP token</strong> ready."
        )
        const smowlAuthenticationRequirement = mlang(
            "Du darfst dich direkt einloggen. Halte dafür deine <strong>TU-ID</strong>, dein <strong>korrektes Passwort</strong> und einen ordnungsgemäß eingerichteten <strong>2FA-TOTP-, 2FA-WebAuthn- oder 2FA-HOTP-Token</strong> bereit.",
            "You may log in immediately. Have your <strong>TU ID</strong>, <strong>correct password</strong> and a properly configured <strong>2FA TOTP, 2FA WebAuthn or 2FA HOTP token</strong> ready."
        )
        const authenticationRequirement = exclusiveSmowl
            ? `{ifnotingroup choice_smowl}${regularAuthenticationRequirement}{/ifnotingroup}{ifingroup choice_smowl}${smowlAuthenticationRequirement}{/ifingroup}`
            : regularAuthenticationRequirement
        const authenticationWarning = mlang(
            "Stelle außerdem vorab das Tastaturlayout ein, mit dem du vertraut bist; die Beschriftung der angeschlossenen Tastatur ist dafür unerheblich. Für selbst zu vertretende Anmeldeverzögerungen besteht grundsätzlich kein Anspruch auf zusätzliche Bearbeitungszeit. Dazu zählen etwa ein fehlendes oder falsch eingegebenes Passwort, ein nicht vertrautes Tastaturlayout oder ein fehlerhaft eingerichteter 2FA-Token. Kann die Prüfung deshalb nicht ordnungsgemäß begonnen werden, kann dies nach Maßgabe der geltenden Prüfungsbestimmungen als Fehlversuch gewertet werden.",
            "Also select the keyboard layout you are most familiar with in advance, regardless of the labels printed on the connected keyboard. There is generally no entitlement to additional working time for login delays for which you are responsible, including those caused by a missing or incorrectly entered password, an unfamiliar keyboard layout or an improperly configured 2FA token. If the exam cannot be started correctly as a result, it may be treated as a failed attempt in accordance with the applicable examination regulations."
        )
        const roomStatusMissingHtml = `{ifnotingrouping room}<div class="alert alert-warning" role="alert"><p><strong>🏢 ${mlang(
            "Die Raumeinteilung ist noch nicht erfolgt.",
            "The room assignment has not been made yet."
        )}</strong></p><p>${mlang(
            "Sobald die Raumeinteilung abgeschlossen ist, findest du hier deinen Prüfungsraum und deine Startzeit.",
            "As soon as the room assignment has been completed, you will find your exam room and start time here."
        )}</p></div>{/ifnotingrouping}`

        const roomStatusDoneHtml = `{ifingrouping room}<div class="alert alert-success" role="alert"><p class="mb-0"><strong>🏢 ${mlang(
            "Die Raumeinteilung <strong>ist</strong> erfolgt.",
            "The room assignment <strong>has been made</strong>."
        )}</strong></p></div>{/ifingrouping}`

        const roomAssignmentHtml = availableRooms
            .map(({ label }) =>
                `{ifingrouping ${roomGroup(label)}}<div class="alert alert-success" role="alert"><p class="mb-0">{ifstudent}${mlang(
                    `Du wirst im Raum <span class="text-primary"><strong>${label}</strong></span> geprüft.`,
                    `You will be examined in room <span class="text-primary"><strong>${label}</strong></span>.`
                )}{/ifstudent}{ifteacher}${mlang(
                    `Du wirst im Raum <span class="text-primary"><strong>${label}</strong></span> Aufsicht machen.`,
                    `You will supervise in room <span class="text-primary"><strong>${label}</strong></span>.`
                )}{/ifteacher}</p></div>{/ifingrouping}`
            )
            .join("\n\n")

        const timeSlotHtml = timeSlots
            .map(slot => {
                const start = moment(`${examDate} ${slot.time}`, "YYYY-MM-DD HH:mm")
                const content = `<div class="alert alert-info" role="alert"><p class="mb-2"><strong><i class="bi bi-clock"></i> ${mlang(
                    `Dein Prüfungsbeginn ist am <span class="text-primary">${start.locale("de").format("D. MMMM [um] HH:mm [Uhr]")}</span>.`,
                    `Your exam starts on <span class="text-primary">${start.locale("en-gb").format("D MMMM YYYY [at] HH:mm")}</span>.`
                )}</strong> ${arrivalInstruction}</p>{ifstudent}<p class="mb-2">${authenticationRequirement}</p><p class="mb-0">${authenticationWarning}</p>{/ifstudent}</div>`

                return timeSlots.length === 1
                    ? content
                    : `{ifingroup ${slot.group}}${content}{/ifingroup}`
            })
            .join("\n\n")

        const buildingBlocksHtml = usedBuildings
            .map(building =>
                `{ifingrouping ${buildingGrouping(building.identifier)}}${renderBuildingCard(building)}{/ifingrouping}`
            )
            .join("\n\n")

        const regularRoomInformationHtml = `${roomStatusMissingHtml}

${roomStatusDoneHtml}

${roomAssignmentHtml}

${buildingBlocksHtml}`

        const roomInformationHtml =
            exclusiveSmowl
                ? `{ifnotingroup choice_smowl}${regularRoomInformationHtml}{/ifnotingroup}`
                : regularRoomInformationHtml

        return `{ifstudent}{ifnotingrouping registration}<div class="alert alert-danger" role="alert"><h4>⚠️ ${mlang(
            "<b>Warnung:</b> Du bist zwar in den Kurs eingeschrieben, aber es gibt ein Problem mit deiner Prüfungsanmeldung (möglicherweise in TUCaN). Falls du dich in TUCaN von der Prüfung abgemeldet hast, kannst du diese Meldung ignorieren.",
            "<b>Warning:</b> You are enrolled in the course, but there is an issue with your exam registration (possibly in TUCaN). If you have withdrawn from the exam in TUCaN, you can ignore this message."
        )}</h4></div>{/ifnotingrouping}{/ifstudent}

${selectionInformationHtml}

${roomInformationHtml}

${timeSlotHtml}
`
    }, [
        arrivalMinutes,
        availableRooms,
        examDate,
        restrictSmowlToChoiceGroup,
        selectedPlatforms,
        timeSlots,
        usedBuildings,
    ])

    const previewContext = useMemo<MoodlePreviewContext>(() => {
        const groups = new Set<string>()
        const groupings = new Set<string>()

        if (previewRegistered) {
            groupings.add("registration")
        }

        if (previewRoomAssigned) {
            groupings.add("room")
        }

        if (previewRoom) {
            groupings.add(roomGroup(previewRoom))
            groupings.add(
                buildingGrouping(getBuildingIdentifierFromRoom(previewRoom))
            )
        }

        if (previewTimeSlot) {
            groups.add(previewTimeSlot)
        }

        if (previewChoiceSmowl) {
            groups.add("choice_smowl")
        }

        return {
            language: previewLanguage,
            role: previewRole,
            groups,
            groupings,
        }
    }, [
        previewChoiceSmowl,
        previewLanguage,
        previewRegistered,
        previewRole,
        previewRoom,
        previewRoomAssigned,
        previewTimeSlot,
    ])

    const renderedInformationPreview = useMemo(
        () => renderMoodlePreview(generatedHtml, previewContext),
        [generatedHtml, previewContext]
    )

    const renderedGuidelinesPreview = useMemo(
        () => renderMoodlePreview(guidelinesHtml, previewContext),
        [guidelinesHtml, previewContext]
    )

    const togglePlatform = (platform: PlatformKey) => {
        setSelectedPlatforms(current =>
            current.includes(platform)
                ? current.filter(entry => entry !== platform)
                : [...current, platform]
        )
    }

    const toggleAid = (aid: AidKey) => {
        setSelectedAidKeys(current =>
            current.includes(aid)
                ? current.filter(entry => entry !== aid)
                : [...current, aid]
        )
    }

    const copyHtml = () => {
        navigator.clipboard.writeText(generatedHtml).then(() => {
            setCopied(true)
            window.setTimeout(() => setCopied(false), 1800)
        })
    }

    const copyGuidelinesHtml = () => {
        navigator.clipboard.writeText(guidelinesHtml).then(() => {
            setGuidelinesCopied(true)
            window.setTimeout(() => setGuidelinesCopied(false), 1800)
        })
    }

    const copyEnrollmentEmailHtml = () => {
        navigator.clipboard.writeText(enrollmentEmailHtml).then(() => {
            setEnrollmentEmailCopied(true)
            window.setTimeout(() => setEnrollmentEmailCopied(false), 1800)
        })
    }

    const downloadGroupImportCsv = () => {
        const blob = new Blob([groupImportCsv], {
            type: "text/csv;charset=utf-8",
        })

        const url = URL.createObjectURL(blob)
        const link = document.createElement("a")

        link.href = url
        link.download = "moodle-groups-import.csv"
        link.click()

        URL.revokeObjectURL(url)
    }

    const removeTimeSlot = (index: number) => {
        setTimeSlots(current => current.filter((_, currentIndex) => currentIndex !== index))
    }

    const addTimeSlot = () => {
        const lastSlot = timeSlots[timeSlots.length - 1]
        const lastStart = moment(`${examDate} ${lastSlot.time}`, "YYYY-MM-DD HH:mm")
        const nextStart = lastStart
            .clone()
            .add(examDurationMinutes, "minutes")
            .add(20, "minutes")

        setTimeSlots([
            ...timeSlots,
            {
                group: `iter${timeSlots.length + 1}`,
                time: nextStart.format("HH:mm"),
            },
        ])
    }

    return (
        <div className="component">
            <Container fluid className="py-3">
                <Row className="g-3">
                    <Col lg={5} className="order-1">
                        <Card>
                            <Card.Header>{t("examPaster.generator")}</Card.Header>
                            <Card.Body>
                                <Row className="g-2">
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>
                                                {i18n.language.startsWith("de")
                                                    ? "E-Mail der Lehrenden"
                                                    : "Teaching staff email"}
                                            </Form.Label>
                                            <Form.Control
                                                type="email"
                                                value={lecturerEmail}
                                                onChange={event =>
                                                    setLecturerEmail(event.target.value)
                                                }
                                                placeholder="name@tu-darmstadt.de"
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>{t("examPaster.examDate")}</Form.Label>
                                            <DatePicker
                                                selected={moment(
                                                    examDate,
                                                    "YYYY-MM-DD",
                                                    true
                                                ).toDate()}
                                                onChange={(date: Date | null) => {
                                                    if (date) {
                                                        setExamDate(moment(date).format("YYYY-MM-DD"))
                                                    }
                                                }}
                                                locale={
                                                    i18n.language.startsWith("de")
                                                        ? "de"
                                                        : "en-GB"
                                                }
                                                dateFormat={
                                                    i18n.language.startsWith("de")
                                                        ? "dd.MM.yyyy"
                                                        : "dd/MM/yyyy"
                                                }
                                                calendarStartDay={1}
                                                customInput={<Form.Control />}
                                                wrapperClassName="w-100"
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>{t("examPaster.entryOffset")}</Form.Label>
                                            <Form.Control
                                                type="number"
                                                value={arrivalMinutes}
                                                onChange={e => setArrivalMinutes(Number(e.target.value))}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>{t("examPaster.duration")}</Form.Label>
                                            <Form.Control
                                                type="number"
                                                value={examDurationMinutes}
                                                onChange={e => setExamDurationMinutes(Number(e.target.value))}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={12}>
                                        <Form.Group>
                                            <Form.Label>{t("examPaster.examLanguage")}</Form.Label>
                                            <Form.Select
                                                value={examLanguage}
                                                onChange={event =>
                                                    setExamLanguage(event.target.value as ExamLanguageKey)
                                                }
                                            >
                                                {examLanguageOptions.map(option => (
                                                    <option value={option.key} key={option.key}>
                                                        {i18n.language.startsWith("de")
                                                            ? option.de
                                                            : option.en}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                            <Form.Text className="text-muted">
                                                {t("examPaster.examLanguageHint")}
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>

                                    <Col md={12}>
                                        <Form.Group>
                                            <Form.Label>{t("examPaster.platforms")}</Form.Label>
                                            <div>
                                                {platformOptions.map(platform => (
                                                    <Form.Check
                                                        key={platform.key}
                                                        type="checkbox"
                                                        id={`platform-${platform.key}`}
                                                        label={t(`examPaster.platform.${platform.key}`)}
                                                        checked={selectedPlatforms.includes(platform.key)}
                                                        onChange={() => togglePlatform(platform.key)}
                                                    />
                                                ))}
                                            </div>
                                        </Form.Group>
                                    </Col>

                                    {selectedPlatforms.includes("smowl") && (
                                        <Col md={12}>
                                            <Form.Check
                                                type="switch"
                                                id="restrict-smowl-to-choice-group"
                                                label={t("examPaster.restrictSmowlToChoiceGroup")}
                                                checked={restrictSmowlToChoiceGroup}
                                                onChange={event =>
                                                    setRestrictSmowlToChoiceGroup(event.target.checked)
                                                }
                                            />
                                            <Form.Text className="text-muted">
                                                {t("examPaster.restrictSmowlToChoiceGroupHint")}
                                            </Form.Text>
                                        </Col>
                                    )}

                                    <Col md={12}>
                                        <Form.Group>
                                            <Form.Label>
                                                {i18n.language.startsWith("de")
                                                    ? "Zugelassene Hilfsmittel"
                                                    : "Permitted aids"}
                                            </Form.Label>
                                            {(["digital", "nonDigital"] as AidCategory[]).map(category => (
                                                <div className="mb-3" key={category}>
                                                    <div className="fw-semibold mb-1">
                                                        {i18n.language.startsWith("de")
                                                            ? category === "digital"
                                                                ? "Digitale Hilfsmittel"
                                                                : "Nicht digitale Hilfsmittel"
                                                            : category === "digital"
                                                                ? "Digital aids"
                                                                : "Non-digital aids"}
                                                    </div>
                                                    {aidOptions
                                                        .filter(option => option.category === category)
                                                        .map(option => (
                                                            <Form.Check
                                                                key={option.key}
                                                                type="checkbox"
                                                                id={`aid-${option.key}`}
                                                                label={
                                                                    i18n.language.startsWith("de")
                                                                        ? option.de
                                                                        : option.en
                                                                }
                                                                checked={selectedAidKeys.includes(option.key)}
                                                                onChange={() => toggleAid(option.key)}
                                                            />
                                                        ))}
                                                </div>
                                            ))}
                                            <Form.Text className="text-muted">
                                                {i18n.language.startsWith("de")
                                                    ? "Die Auswahl wird direkt in die Hilfsmittelzeilen der Prüfungsrichtlinien übernommen."
                                                    : "The selection is inserted directly into the aids rows of the examination guidelines."}
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <hr />

                                <Form.Label>{t("examPaster.timeSlots")}</Form.Label>
                                {timeSlots.map((slot, index) => (
                                    <Row className="g-2 mb-2" key={index}>
                                        <Col md={5}>
                                            <Form.Control
                                                value={slot.group}
                                                onChange={e => {
                                                    const next = [...timeSlots]
                                                    next[index].group = e.target.value
                                                    setTimeSlots(next)
                                                }}
                                            />
                                        </Col>

                                        <Col md={5}>
                                            <Form.Control
                                                type="time"
                                                value={slot.time}
                                                onChange={e => {
                                                    const next = [...timeSlots]
                                                    next[index].time = e.target.value
                                                    setTimeSlots(next)
                                                }}
                                            />
                                        </Col>

                                        <Col md={2}>
                                            <Button
                                                variant="outline-danger"
                                                className="w-100"
                                                onClick={() => removeTimeSlot(index)}
                                            >
                                                <i className="bi bi-trash"></i>
                                            </Button>
                                        </Col>
                                    </Row>
                                ))}

                                <Button
                                    size="sm"
                                    variant="outline-primary"
                                    onClick={addTimeSlot}
                                >
                                    {t("examPaster.addTimeSlot")}
                                </Button>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col xs={12} className="order-3">
                        <Card className="mt-3">
                            <Card.Header>{t("examPaster.preview.title")}</Card.Header>
                            <Card.Body>
                                <Row className="g-3">
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>{t("examPaster.preview.language")}</Form.Label>
                                            <Form.Select
                                                value={previewLanguage}
                                                onChange={event =>
                                                    setPreviewLanguage(event.target.value as PreviewLanguage)
                                                }
                                            >
                                                <option value="de">Deutsch</option>
                                                <option value="en">English</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>{t("examPaster.preview.role")}</Form.Label>
                                            <Form.Select
                                                value={previewRole}
                                                onChange={event =>
                                                    setPreviewRole(event.target.value as PreviewRole)
                                                }
                                            >
                                                <option value="student">{t("examPaster.preview.student")}</option>
                                                <option value="teacher">{t("examPaster.preview.teacher")}</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>

                                    <Col xs={12}>
                                        <Form.Check
                                            type="switch"
                                            id="preview-registered"
                                            label={t("examPaster.preview.registered")}
                                            checked={previewRegistered}
                                            onChange={event => setPreviewRegistered(event.target.checked)}
                                        />
                                        <Form.Check
                                            type="switch"
                                            id="preview-room-assigned"
                                            label={t("examPaster.preview.roomAssigned")}
                                            checked={previewRoomAssigned}
                                            onChange={event => setPreviewRoomAssigned(event.target.checked)}
                                        />
                                        <Form.Check
                                            type="switch"
                                            id="preview-choice-smowl"
                                            label={t("examPaster.preview.choiceSmowl")}
                                            checked={previewChoiceSmowl}
                                            onChange={event => setPreviewChoiceSmowl(event.target.checked)}
                                        />
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>{t("examPaster.preview.room")}</Form.Label>
                                            <Form.Select
                                                value={previewRoom}
                                                onChange={event => {
                                                    setPreviewRoom(event.target.value)
                                                    if (event.target.value) {
                                                        setPreviewRoomAssigned(true)
                                                    }
                                                }}
                                                disabled={previewChoiceSmowl}
                                            >
                                                <option value="">{t("examPaster.preview.noRoom")}</option>
                                                {availableRooms.map(({label}) => (
                                                    <option value={label} key={label}>{label}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>{t("examPaster.preview.timeSlot")}</Form.Label>
                                            <Form.Select
                                                value={previewTimeSlot}
                                                onChange={event => setPreviewTimeSlot(event.target.value)}
                                            >
                                                <option value="">{t("examPaster.preview.noTimeSlot")}</option>
                                                {timeSlots.map(slot => (
                                                    <option value={slot.group} key={slot.group}>
                                                        <strong>{slot.group}</strong> {slot.time}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <hr />

                                <h5>{t("examPaster.preview.information")}</h5>
                                <div
                                    className="border rounded p-3 mb-4"
                                    dangerouslySetInnerHTML={{__html: renderedInformationPreview}}
                                />

                                <h5>{t("examPaster.preview.guidelines")}</h5>
                                <div
                                    className="border rounded p-3 overflow-auto"
                                    dangerouslySetInnerHTML={{__html: renderedGuidelinesPreview}}
                                />
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col lg={7} className="order-2">
                        <Card className="mb-3">
                            <Card.Header className="d-flex justify-content-between">
                                <span>{t("examPaster.groupCsv")}</span>
                                <Button
                                    size="sm"
                                    variant="outline-secondary"
                                    onClick={downloadGroupImportCsv}
                                >
                                    <i className="bi bi-download"></i>{" "}
                                    {t("examPaster.download")}
                                </Button>
                            </Card.Header>

                            <Card.Body>
                                <Form.Control
                                    as="textarea"
                                    rows={6}
                                    readOnly
                                    value={groupImportCsv}
                                    style={{
                                        fontFamily: "monospace",
                                        fontSize: "0.875rem",
                                        whiteSpace: "pre",
                                    }}
                                />
                            </Card.Body>
                        </Card>

                        <Card>
                            <Card.Header className="d-flex justify-content-between">
                                <span>{t("examPaster.generatedHtml")}</span>
                                <Button
                                    size="sm"
                                    variant={copied ? "success" : "outline-secondary"}
                                    onClick={copyHtml}
                                >
                                    <i className={`bi ${copied ? "bi-check-lg" : "bi-clipboard"}`}></i>{" "}
                                    {t(copied ? "examPaster.copied" : "examPaster.copy")}
                                </Button>
                            </Card.Header>

                            <Card.Body>
                                <Form.Control
                                    as="textarea"
                                    rows={10}
                                    readOnly
                                    value={generatedHtml}
                                    style={{
                                        fontFamily: "monospace",
                                        fontSize: "0.875rem",
                                        whiteSpace: "pre",
                                    }}
                                />
                            </Card.Body>
                        </Card>

                        <Card className="mt-3">
                            <Card.Header className="d-flex justify-content-between">
                                <span>
                                    {i18n.language.startsWith("de")
                                        ? "Einschreibe-E-Mail (Moodle-HTML)"
                                        : "Enrollment email (Moodle HTML)"}
                                </span>
                                <Button
                                    size="sm"
                                    variant={
                                        enrollmentEmailCopied
                                            ? "success"
                                            : "outline-secondary"
                                    }
                                    onClick={copyEnrollmentEmailHtml}
                                >
                                    <i className={`bi ${enrollmentEmailCopied ? "bi-check-lg" : "bi-clipboard"}`}></i>{" "}
                                    {t(enrollmentEmailCopied ? "examPaster.copied" : "examPaster.copy")}
                                </Button>
                            </Card.Header>

                            <Card.Body>
                                <Form.Control
                                    as="textarea"
                                    rows={24}
                                    readOnly
                                    value={enrollmentEmailHtml}
                                    style={{
                                        fontFamily: "monospace",
                                        fontSize: "0.875rem",
                                        whiteSpace: "pre",
                                    }}
                                />
                            </Card.Body>
                        </Card>

                        <Card className="mt-3">
                            <Card.Header className="d-flex justify-content-between">
                                <span>{t("examPaster.guidelinesHtml")}</span>
                                <Button
                                    size="sm"
                                    variant={guidelinesCopied ? "success" : "outline-secondary"}
                                    onClick={copyGuidelinesHtml}
                                >
                                    <i className={`bi ${guidelinesCopied ? "bi-check-lg" : "bi-clipboard"}`}></i>{" "}
                                    {t(guidelinesCopied ? "examPaster.copied" : "examPaster.copy")}
                                </Button>
                            </Card.Header>

                            <Card.Body>
                                <Form.Control
                                    as="textarea"
                                    rows={5}
                                    readOnly
                                    value={guidelinesHtml}
                                    style={{
                                        fontFamily: "monospace",
                                        fontSize: "0.875rem",
                                        whiteSpace: "pre",
                                    }}
                                />
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    )
}

export default ExamPasterComponent
